// FarmhouseDetailsModal.js
import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Alert,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchFarmhouseDetails } from "../../slices/farmhouse/thunk";
import { api } from "../../config";

const formatPriority = (priority) => {
    const priorityMap = {
        'HIGH': 'High',
        'MEDIUM': 'Medium',
        'LOW': 'Low'
    };
    return priorityMap[priority] || priority;
};

const FarmhouseDetailsModal = ({ isOpen, toggle, farmhouseId }) => {
    const dispatch = useDispatch();
    const { farmhouseDetails, loading } = useSelector((state) => ({
        farmhouseDetails: state.Farmhouse.farmhouseDetails,
        loading: state.Farmhouse.loading,
    }));

    useEffect(() => {
        if (isOpen && farmhouseId) {
            dispatch(fetchFarmhouseDetails(farmhouseId));
        }
    }, [dispatch, farmhouseId, isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="full-height-model">
            <ModalHeader toggle={toggle}>
                {loading ? "Loading..." : (farmhouseDetails?.name || "Farmhouse Details")}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center p-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading farmhouse details...</p>
                    </div>
                ) : farmhouseDetails ? (
                    <Container fluid>
                        <Row>
                            <Col lg={8}>
                                {/* Basic Info */}
                                <div className="mb-4 p-3 border rounded">
                                    <h5 className="mb-3">Basic Information</h5>
                                    <div className="mb-3">
                                        <strong>Name:</strong> {farmhouseDetails.name}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Farm No:</strong> {farmhouseDetails.farmNo || "N/A"}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Priority:</strong>
                                        <Badge
                                            color={
                                                farmhouseDetails.priority === "HIGH" ? "danger" :
                                                    farmhouseDetails.priority === "MEDIUM" ? "warning" : "success"
                                            }
                                            className="ms-2"
                                        >
                                            {formatPriority(farmhouseDetails.priority)}
                                        </Badge>
                                    </div>
                                    <div className="mb-3">
                                        <strong>Description:</strong>
                                        <p className="text-muted mb-0">{farmhouseDetails.description}</p>
                                    </div>
                                </div>

                                {/* Location */}
                                <div className="mb-4 p-3 border rounded">
                                    <h5 className="mb-3">Location</h5>
                                    <div className="mb-3">
                                        <strong>Address:</strong>
                                        <p className="text-muted mb-0">
                                            {farmhouseDetails.location?.address}<br />
                                            {farmhouseDetails.location?.city}, {farmhouseDetails.location?.state}
                                        </p>
                                    </div>
                                    {farmhouseDetails.location?.mapUrl && (
                                        <div className="mb-3">
                                            <strong>Map URL:</strong> <a href={farmhouseDetails.location.mapUrl} target="_blank" rel="noopener noreferrer" className="text-primary text-break">{farmhouseDetails.location.mapUrl}</a>
                                        </div>
                                    )}
                                    {farmhouseDetails.location?.latitude && farmhouseDetails.location?.longitude && (
                                        <div className="mt-3">
                                            <iframe
                                                title="Map Preview"
                                                width="100%"
                                                height="250"
                                                frameBorder="0"
                                                scrolling="no"
                                                marginHeight="0"
                                                marginWidth="0"
                                                src={`https://maps.google.com/maps?q=${farmhouseDetails.location.latitude},${farmhouseDetails.location.longitude}&z=15&output=embed`}
                                                className="rounded border"
                                            ></iframe>
                                        </div>
                                    )}
                                </div>

                                {/* Amenities Section */}
                                {farmhouseDetails.farmhouseAmenities && farmhouseDetails.farmhouseAmenities.length > 0 && (
                                    <div className="mb-4 p-3 border rounded">
                                        <h5 className="mb-3">Amenities</h5>
                                        <Row>
                                            {farmhouseDetails.farmhouseAmenities.map((amenity, index) => (
                                                <Col md={6} key={index} className="mb-2">
                                                    <div className="p-2 border rounded">
                                                        <strong>{amenity.amenity?.name}</strong>
                                                        <div className="text-muted small">
                                                            Category: {amenity.category?.name} | Qty: {amenity.quantity}
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                            </Col>

                            <Col lg={4}>
                                {/* Status & Capacity */}
                                <div className="mb-4 p-3 border rounded">
                                    <h5 className="mb-3">Status & Capacity</h5>
                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <Badge color={farmhouseDetails.status ? "success" : "danger"} className="ms-2">
                                            {farmhouseDetails.status ? "Active" : "Inactive"}
                                        </Badge>
                                    </div>
                                    <div className="mb-3">
                                        <strong>Max Persons:</strong> {farmhouseDetails.maxPersons}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Bedrooms:</strong> {farmhouseDetails.bedrooms}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Check-in:</strong> {farmhouseDetails.checkInFrom}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Check-out:</strong> {farmhouseDetails.checkOutTo}
                                    </div>
                                </div>

                                {/* Price Options */}
                                <div className="mb-4 p-3 border rounded">
                                    <h5 className="mb-3">Price Options</h5>
                                    <div className="price-options-container">
                                        {farmhouseDetails.priceOptions && farmhouseDetails.priceOptions.map((option, index) => (
                                            <div
                                                key={index}
                                                className={`${index !== farmhouseDetails.priceOptions.length - 1 ? 'mb-2 pb-2' : ''}`}
                                                style={{
                                                    borderBottom: index === farmhouseDetails.priceOptions.length - 1 ? 'none' : '1px solid #dee2e6'
                                                }}
                                            >
                                                <strong>{option.category.replace(/_/g, ' ')}</strong>
                                                <div className="text-muted">
                                                    Price: ₹{option.price}<br />
                                                    Max People: {option.maxPeople}
                                                    {option.extraPersonPrice != null && option.extraPersonPrice !== '' && (
                                                        <>
                                                            <br />
                                                            Extra Person: ₹{option.extraPersonPrice}/person
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Images Section */}
                        {farmhouseDetails.images && farmhouseDetails.images.length > 0 && (
                            <div className="mb-4 p-3 border rounded">
                                <h5 className="mb-3">Media Files</h5>
                                <Row>
                                    {[...farmhouseDetails.images].sort((a, b) => (a.priority || 0) - (b.priority || 0)).map((image, index) => {

                                        const imagePath = image.imagePath || '';
                                        const isVideo = imagePath.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i) || image.type === 'video' || image.mimeType?.startsWith('video/');
                                        const isImage = imagePath.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) || image.type === 'image' || image.mimeType?.startsWith('image/');
                                        const baseImgUrl = api.IMG_URL;
                                        // In dev, if IMG_URL is empty (intentional for proxy), use empty base
                                        // If in prod, use full URL
                                        let baseUrl = baseImgUrl && baseImgUrl.endsWith('/') ? baseImgUrl.slice(0, -1) : (baseImgUrl || "");

                                        const path = imagePath && imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                                        const imageUrl = `${baseUrl}${path}`;

                                        return (
                                            <Col md={3} key={index} className="mb-3">
                                                {isVideo ? (
                                                    <video
                                                        src={imageUrl}
                                                        className="img-fluid rounded border"
                                                        style={{
                                                            height: '150px',
                                                            width: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        controls
                                                        muted
                                                    />
                                                ) : isImage ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={`Farmhouse ${index + 1}`}
                                                        className="img-fluid rounded border"
                                                        style={{
                                                            height: '150px',
                                                            width: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            // If image fails to load, check if it might be a video
                                                            const src = e.target.src;
                                                            if (src.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i)) {
                                                                e.target.insertAdjacentHTML('beforebegin', `
                                                                    <video 
                                                                        src="${imageUrl}" 
                                                                        class="img-fluid rounded border" 
                                                                        style="height: 150px; width: 100%; object-fit: cover;" 
                                                                        controls 
                                                                        muted
                                                                    ></video>
                                                                `);
                                                                e.target.remove();
                                                            } else {
                                                                // Generic file fallback for broken links
                                                                e.target.style.display = 'none';
                                                                const parent = e.target.parentElement;
                                                                parent.innerHTML = `
                                                                    <div class="d-flex align-items-center justify-content-center rounded border" style="height: 150px; width: 100%; background-color: #f5f5f5">
                                                                        <div class="text-center px-2 w-100">
                                                                            <i class="ri-file-line fs-1 text-muted"></i>
                                                                            <div class="small text-muted mt-2 text-truncate">${imagePath.split('/').pop() || 'File'}</div>
                                                                        </div>
                                                                    </div>
                                                                `;
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded border"
                                                        style={{
                                                            height: '150px',
                                                            width: '100%',
                                                            backgroundColor: '#f5f5f5',
                                                        }}
                                                    >
                                                        <div className="text-center w-100 px-2">
                                                            <i className="ri-file-line fs-1 text-muted"></i>
                                                            <div className="small text-muted mt-2 text-truncate" title={imagePath.split('/').pop()}>
                                                                {imagePath.split('/').pop() || 'File'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        )}

                    </Container>
                ) : (
                    <Alert color="danger">Farmhouse not found</Alert>
                )}
            </ModalBody>
        </Modal>
    );
};

export default FarmhouseDetailsModal;